<template>
  <div class="project">
    <Breadcrumb v-if="!api">
      <template #actions>
        <el-button @click="handleBack">返回首页</el-button>
      </template>
    </Breadcrumb>
    <div :class="['project-table', { 'project-table-padding': !api }]">
      <SearchTable
        ref="SearchTableRef"
        :columns="tableColumns"
        :default-sort="{ prop: 'create_time', order: 'descending' }"
        :api="api || fetchList"
        :getRows="getRows"
        :showSearch="!api"
      >
        <template #action="{ scope }">
          <el-space size="large" class="actions">
            <el-tooltip content="修改信息">
              <el-icon @click="handleUpdate(scope.row)"><Tickets /></el-icon>
            </el-tooltip>
            <el-tooltip content="继续编辑">
              <el-icon v-if="scope.row.isProject" @click="handleWriting(scope.row)"><Edit  /></el-icon>
            </el-tooltip>
            <el-popconfirm
              width="200"
              title="确定删除该项目?"
              v-if="scope.row.isProject"
              @confirm="handleDelete(scope.row.id)"
            >
              <template #reference>
                <div style="display: flex; align-items: center;">
                  <el-tooltip content="删除">
                    <el-icon><Delete /></el-icon>
                  </el-tooltip>
                </div>
              </template>
              <template #actions="{ confirm, cancel }">
                <el-button size="small" @click="cancel">取消</el-button>
                <el-button
                  type="danger"
                  size="small"
                  @click="confirm"
                >
                  删除
                </el-button>
              </template>
            </el-popconfirm>
          </el-space>
        </template>
      </SearchTable>
      <CreateProject v-model:visible="createProjectVisible" isEdit :projectData="updateProjectData" @success="handleUpdateSuccess" />
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { fetchList, fetchDownload, fetchDownloadStructure, fetchDelete } from '@/api/project'
import { ElMessage, ElLoading } from 'element-plus'
import { Edit, Delete, Tickets } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { useGlobalStore } from '@/store/modules/global'
import dayjs from 'dayjs'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchTable from '@/components/SearchTable'
import CreateProject from '@/components/CreateProject/index.vue'

const router = useRouter()
const globalStore = useGlobalStore()
const resolveVisibleStatus = (statusCode) => {
  const code = Number(statusCode)
  return code >= 2 ? '2' : '1'
}

const props = defineProps({
  api: {
    type: Function,
    default: null
  }
})

const SearchTableRef = ref(null)
const tableColumns = reactive([
  // {
  //   prop: 'id',
  //   label: 'id',
  //   search: {
  //     hide: true
  //   }
  // },
  {
    prop: 'projectName',
    label: '项目名称',
    align: 'center',
    resizable: true,
  },
  {
    prop: 'projectCustomers',
    label: '所属客户',
    align: 'center',
    width: '120px',
  },
  {
    prop: 'serialNumber',
    label: '案号',
    align: 'center',
    resizable: true,
  },
  {
    prop: 'statusCode',
    label: '当前环节',
    align: 'center',
    resizable: true,
    type: 'select',
    formatter: (record, column, v) => {
      return globalStore.dicts.statusEnum[resolveVisibleStatus(v)] || '-'
    },
    search: {
      options: Object.keys(globalStore.dicts.statusEnum).map(k => ({ label: globalStore.dicts.statusEnum[k], value: k})),
    }
  },
  {
    prop: 'create_time',
    label: '立项时间',
    align: 'center',
    type: 'date',
    sortable: 'custom',
    resizable: true,
    width: '180px',
    formatter: (record) => record.createTime ? dayjs(record.createTime).format('YYYY-MM-DD HH:mm:ss') : '-',
    search: {
      type: 'daterange',
      transform: (v) => ({
        startTime: v[0].getTime(),
        endTime: v[1].getTime() + 24 * 3600000 - 1
      })
    }
  },
  {
    prop: 'comment',
    label: '备注',
    align: 'center',
    search: { hide: true }
  },
  {
    prop: 'action',
    label: '操作',
    width: '180px',
    slot: true,
    align: 'center',
    search: { hide: true }
  }
])
const createProjectVisible = ref(false)
const updateProjectData = ref(null)

const handleUpdate = (item) => {
  createProjectVisible.value = true
  updateProjectData.value = item
}

const handleUpdateSuccess = () => {
  updateProjectData.value = null
  SearchTableRef.value?.refresh()
}

const handleWriting = (item) => {
  const code = Number(item.statusCode)
  if (code >= 2) {
    router.push(`/writing/step2?id=${item.id}`)
  } else {
    router.push(`/writing/step1?id=${item.id}`)
  }
}

const handlePreview = (item) => {
  window.open(item.previewUrl)
}

const promiseDownload = (promise, download) => {
  const loading = ElLoading.service({
    lock: true,
    text: '正在下载...',
    background: 'rgba(255, 255, 255, 0.7)'
  })

  promise
    .then(blob => {
      if (blob) {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        ElMessage.success('下载成功')
        loading.close()
      } else {
        loading.close()
      }
    })
    .catch(() => {
      loading.close()
    })
}

const handleDownload = (item) => {
  const files = []
  let download = ''
  if (item.isProject) {
    const uploadFiles = []
    const resultFiles = []
    item.uploadFiles?.forEach(f => {
      uploadFiles.push(f.fileUrl)
    })
    item.resultFiles?.forEach(f => {
      resultFiles.push(f.fileUrl)
    })
    if (!uploadFiles.length && !resultFiles.length) {
      ElMessage.warning('项目暂无文件')
      return
    }
    const urls = {
      '上传文件': uploadFiles,
      '终稿文件': resultFiles
    }

    download = `${item.projectName}.zip`
    promiseDownload(fetchDownloadStructure({ urls }), download)
    return
  }
  if (!item.isLeaf) {
    item.children.forEach(f => {
      files.push(f.fileUrl)
    })
    download = `${item.parentName ? `${item.parentName}-` : ''}${item.projectName}.zip`
    promiseDownload(fetchDownloadStructure({ urls: {[item.projectName]: files} }), download)
  } else {
    files.push(item.fileUrl)
    download = item.fileName
    promiseDownload(fetchDownload(files), download)
  }
}

const handleDelete = (id) => {
  fetchDelete([id]).then(res => {
    if (res) {
      ElMessage.success('删除成功')
      SearchTableRef.value.delete()
    }
  })
}

const handleBack = () => {
  router.replace('/home')
}

const getRows = (rows) => {
  return rows.map(row => {
    const children = []

    if (row.uploadFiles?.length) {
      children.push({
        id: row.id + 'uploadFiled',
        projectName: '上传文件',
        parentName: row.projectName,
        children: row.uploadFiles.map(v => ({
          ...v,
          projectName: v.fileName,
          projectCreator: v.createBy,
          isLeaf: true
        }))
      })
    }
    if (row.resultFiles?.length) {
      children.push({
        id: row.id + 'resultFiles',
        projectName: '终稿文件',
        parentName: row.projectName,
        children: row.resultFiles.map(v => ({
          ...v,
          projectName: v.fileName,
          projectCreator: v.createBy,
          isLeaf: true
        }))
      })
    }
    return { ...row, isProject: true, children }
  })
}

</script>

<style lang="scss" scoped>
.project {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  .project-table {
    flex: 1;
    &.project-table-padding {
      padding: 3% 6%;
    }
  }
  .actions {
    gap: 0px 24px !important;
    :deep(.el-icon) {
      font-size: 18px;
      cursor: pointer;
    }
  }
  :deep(.el-table) {
    .el-table__header {
      .el-table__cell {
        font-weight: bold;
      }
    }
  }
}
</style>
